import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, X, Loader2 } from "lucide-react";
import { SharedResourceItem } from "@/modules/hub/features/HubDashboard/api/getHubDashboard/types";
import { deleteResourceShare } from "@/api/deleteResourceShare/deleteResourceShareApi";
import { Button } from "@/components/ui/button";

interface SharedResourceCardProps {
  resource: SharedResourceItem;
  onRemove?: () => void;
}

export function SharedResourceCard({
  resource,
  onRemove,
}: SharedResourceCardProps) {
  const [isRemoving, setIsRemoving] = useState(false);

  const contentType = (resource.resourceType || "document").toLowerCase();

  const formatSharerNames = () => {
    const sharers = resource.sharedBy;
    if (sharers.length === 0) return "";
    if (sharers.length === 1) {
      return `Shared by ${sharers[0].firstName} ${sharers[0].lastName}`;
    }
    if (sharers.length === 2) {
      return `Shared by ${sharers[0].firstName} and ${sharers[1].firstName}`;
    }
    return `Shared by ${sharers[0].firstName} and ${sharers.length - 1} others`;
  };

  const handleRemove = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (resource.sharedBy.length === 0) return;

    setIsRemoving(true);
    try {
      // Delete all shares for this resource
      await Promise.all(
        resource.sharedBy.map((sharer) => deleteResourceShare(sharer.shareUuid)),
      );
      onRemove?.();
    } catch (error) {
      console.error("Error removing share:", error);
    } finally {
      setIsRemoving(false);
    }
  };

  const isPartnerPage =
    resource.resourceType === "Partner Page" && resource.contentUrl;

  const cardContent = (
    <>
      <div className="relative aspect-video">
        {resource.thumbnailUrl ? (
          <img
            alt={resource.title}
            className="object-cover rounded-t-md w-full h-full"
            src={resource.thumbnailUrl}
          />
        ) : resource.resourceType === "File" ? (
          <div className="w-full h-full rounded-t-md flex items-center justify-center bg-gradient-to-b from-red-300 to-red-400 dark:from-red-400 dark:to-red-600 relative overflow-hidden">
            <svg
              className="absolute inset-0 w-full h-full opacity-20"
              preserveAspectRatio="none"
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0,30 Q25,20 50,30 T100,30 L100,100 L0,100 Z"
                fill="white"
              />
              <path
                d="M0,60 Q25,50 50,60 T100,60 L100,100 L0,100 Z"
                fill="white"
                opacity="0.5"
              />
              <path
                d="M0,85 Q25,80 50,85 T100,85 L100,100 L0,100 Z"
                fill="white"
                opacity="0.3"
              />
            </svg>
            <span
              className="inline-block relative z-10"
              dangerouslySetInnerHTML={{
                __html: resource.primaryIcon ?? "",
              }}
              style={{
                filter: "brightness(0) invert(1)",
                opacity: 0.9,
              }}
            />
          </div>
        ) : (
          <div className="w-full h-full rounded-t-md flex items-center justify-center bg-gray-100 dark:bg-gray-700">
            <span
              className="inline-block"
              dangerouslySetInnerHTML={{
                __html: resource.primaryIcon ?? "",
              }}
            />
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span
            className="text-xs font-light px-2 py-0.5 rounded-full"
            style={{
              backgroundColor: resource.backgroundColor ?? "#ccc",
              color: resource.textColor ?? "#000",
              border: resource.borderColor
                ? `1px solid ${resource.borderColor}`
                : "none",
            }}
          >
            {contentType.charAt(0).toUpperCase() + contentType.slice(1)}
          </span>
        </div>

        <h3 className="font-medium text-sm text-gray-900 dark:text-white line-clamp-2">
          {resource.title}
        </h3>

        <div className="mt-2 text-xs text-blue-600 dark:text-blue-400 font-medium">
          {formatSharerNames()}
        </div>

        <div className="mt-2 text-xs text-gray-500 font-light">
          <span className="flex items-center gap-1">
            <Eye className="h-3 w-3" />
            {resource.viewCount} views
          </span>
        </div>
      </div>
    </>
  );

  return (
    <div className="block group rounded-md border border-gray-100 dark:border-gray-800 hover:shadow-sm transition-all duration-300 bg-white dark:bg-gray-800 relative">
      {/* Remove Button */}
      <div className="absolute top-2 right-2" style={{ zIndex: 1 }}>
        <Button
          className="h-7 w-7 p-0 bg-white/90 dark:bg-gray-800/90 hover:bg-red-50 dark:hover:bg-red-900/30 border border-gray-200 dark:border-gray-700"
          disabled={isRemoving}
          onClick={handleRemove}
          size="sm"
          title="Remove from shared with you"
          variant="ghost"
        >
          {isRemoving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-500" />
          ) : (
            <X className="h-3.5 w-3.5 text-gray-500 hover:text-red-500" />
          )}
        </Button>
      </div>

      {isPartnerPage ? (
        <a
          className="block"
          href={resource.contentUrl || ""}
          rel="noopener noreferrer"
          target="_blank"
        >
          {cardContent}
        </a>
      ) : (
        <Link className="block" to={`/hub/resources/${resource.slug}`}>
          {cardContent}
        </Link>
      )}
    </div>
  );
}
